// packages/web-ui/src/state/changeset-previews.ts
//
// Lightweight singleton store for the per-changeset `ReactFlowLayoutJson`
// previews delivered via the `kb:changesetPreview` SSE event. Kept
// separate from the `inline-cards` slice (envelope props are stable
// identity keys only — the actual diff payload is big and lives here).
//
// Usage:
//  - the SSE dispatcher calls `setChangesetPreviewLayout(changesetId, layout)`
//    when a `kb:changesetPreview` event arrives,
//  - the `<ChangesetPreviewCard>` reads via `selectChangesetPreviewLayout`
//    inside its `useStore` subscription.
//
// This is intentionally a vanilla zustand store, NOT a slice composed
// into the root store, so we don't widen the root store surface for a
// feature that has no actions and zero coupling to other slices.

import { createStore } from 'zustand/vanilla';
import type { StoreApi } from 'zustand';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';

export interface ChangesetPreviewsState {
  /** Keyed by `changesetId`. */
  layoutById: Record<string, ReactFlowLayoutJson>;
  /** Setter — replaces any prior layout for the same changeset id. */
  setLayout: (changesetId: string, layout: ReactFlowLayoutJson) => void;
  /** Test/debug helper. */
  _reset: () => void;
}

export function createChangesetPreviewsStore(): StoreApi<ChangesetPreviewsState> {
  return createStore<ChangesetPreviewsState>()((set) => ({
    layoutById: {},
    setLayout: (changesetId, layout) =>
      set((s) => ({
        ...s,
        layoutById: { ...s.layoutById, [changesetId]: layout },
      })),
    _reset: () => set({ layoutById: {} } as ChangesetPreviewsState),
  }));
}

let _singleton: StoreApi<ChangesetPreviewsState> | null = null;

/** Default singleton — used by the SSE dispatcher + the card component. */
export function getChangesetPreviewsStore(): StoreApi<ChangesetPreviewsState> {
  _singleton ??= createChangesetPreviewsStore();
  return _singleton;
}

/** Test-only — wipe the singleton between cases. */
export function _resetChangesetPreviewsStoreForTests(): void {
  _singleton = null;
}

export function selectChangesetPreviewLayout(changesetId: string) {
  return (s: ChangesetPreviewsState): ReactFlowLayoutJson | undefined =>
    s.layoutById[changesetId];
}
